/**
 * The list view — the same tasks in one table.
 *
 * Default on `/studio/tasks`: triage is a table, not a multi-column board. Columns
 * stay few — Task, Notes, Client, Who, Due, Priority, Queue. Client / Who /
 * Due heads toggle asc/desc via `?sort=` and `?dir=` (same URL-as-state rule as
 * the filter seg). Filters sit above this table on the page, not inside it.
 *
 * Notes show `blocked_reason ?? instructions`, one line (`.tlist__note`); hover
 * keeps the full string. Row rails match the board cards (`taskState`).
 *
 * Who / Due / Notes (when not a block reason) / Priority / Queue open an
 * `ActionDialog` over the cell — Assign or a one-field Update (`only`) — so
 * triage edits stay on the list. Undo (#12/#25) covers assignee, priority, due
 * and queue after a successful save.
 *
 * The Who cell also carries the primary state move (Claim / Start / …) beside
 * the name — no separate Actions column.
 *
 * Checkboxes + `BulkBar` (#22) let a coordinator assign, reschedule, requeue or
 * block several rows without opening each one.
 */

import { Link } from '@tanstack/react-router'
import { forwardRef, useEffect, useRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { taskMachine, type TaskQueue, type TaskView } from '@fareast/shared'

import { messageOf } from '../../../api/problem.js'
import { isRestrictedStaff, usePrincipal } from '../../../auth/principal.js'
import { Button } from '../../../components/portal/Button.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { ScrollX } from '../../../components/portal/Layout.js'
import { Notice } from '../../../components/portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import { Linkish, Muted, Ref, Small, Xs } from '../../../components/portal/Text.js'
import { ActionDialog } from '../../../components/studio/ActionDialog.js'
import { CellAssign, CellDue, CellEdit, CellStatus } from '../../../components/studio/CellEdit.js'
import { ClientLink } from '../../../components/studio/ClientLink.js'
import { TaskWhen, holdLine, taskState } from '../../../components/studio/TaskCardFull.js'
import { QUEUE_WORD, STATUS_WORD, priorityWord } from '../../../components/studio/taskWords.js'
import { useFormat } from '../../../format/useFormat.js'
import { STUDIO_COLUMN, type ListDir, type ListSort, type TaskPages } from './board.js'
import { useFieldSave, type FieldUndo, type TaskMoved } from './fieldSave.js'
import { RowActions } from './RowActions.js'
import { useTaskNotice } from './taskNotice.js'

/** No rows ticked, as one object — a new `Set()` per render is a new prop. */
const EMPTY: ReadonlySet<string> = new Set()

export type TaskListProps = {
  /** The rows, a page at a time, in the server's order (`board.ts` `useTaskPages`). */
  readonly pages: TaskPages
  /**
   * How many tasks the filters match, from `tasks_summary` asked with the same ones:
   * the "of" in "Showing 50 of 126". Undefined until it lands.
   */
  readonly matching: { readonly count: number; readonly capped: boolean } | undefined
  /** A client's slug as the company is called. */
  readonly clientName: (slug: string) => string
  readonly at: Date
  readonly sort: ListSort
  readonly dir: ListDir
  readonly onSort: (column: ListSort) => void
  /**
   * Which rows are ticked — the **page's** state, not the table's.
   *
   * The bulk bar it feeds is docked at the bottom of the window beside the notice
   * line (`taskNotice.tsx` `TaskDock`), and a bar drawn outside this component
   * cannot read a selection held inside it. The table still owns every control that
   * changes it: the head checkbox and the one on each row.
   */
  readonly selected?: ReadonlySet<string> | undefined
  readonly onSelected?: ((next: ReadonlySet<string>) => void) | undefined
}

export function TaskList({
  pages,
  matching,
  clientName,
  at,
  sort,
  dir,
  onSort,
  selected = EMPTY,
  onSelected,
}: TaskListProps) {
  const principal = usePrincipal()
  const clientMuted = isRestrictedStaff(principal)
  const viewerId = principal.kind === 'staff_user' ? principal.userId : null
  /*
   * What just happened is the **page's** line, not the table's: a board card does
   * the same moves and owed the same sentence, and two lines meant Undo could mean
   * the step before last. `taskNotice.tsx` says why at length.
   */
  const notice = useTaskNotice()
  /* The sentence and the way back are `fieldSave.ts`'s — the board writes the same. */
  const field = useFieldSave()
  const format = useFormat()

  /* The server's order, as it came: nothing here sorts, filters or counts (session 42). */
  const rows = pages.rows
  const rowRefs = rows.map((task) => task.ref)

  /*
   * Where Load more was pressed, so the first row it brings takes the focus: a
   * keyboard reader who pressed it is otherwise left on a button at the foot of a
   * table that just grew above it.
   */
  const firstNew = useRef<number | null>(null)
  useEffect(() => {
    const from = firstNew.current
    if (from === null || rows.length <= from) return
    firstNew.current = null
    const ref = rows[from]?.ref
    if (ref !== undefined) document.getElementById(rowTitleId(ref))?.focus()
  }, [rows])

  if (pages.isPending) return <Empty>Reading the tasks…</Empty>
  // A first page that did not come is not an empty list: say so, and offer it again.
  if (rows.length === 0 && pages.failed !== null) {
    return <LoadMore pages={pages} onLoadMore={pages.loadMore} what="tasks" />
  }
  if (rows.length === 0) {
    return <Empty>Nothing is in hand under this filter.</Empty>
  }

  const loadMore = (): void => {
    firstNew.current = rows.length
    pages.loadMore()
  }

  const allOn = rows.length > 0 && rows.every((task) => selected.has(task.ref))
  const someOn = rows.some((task) => selected.has(task.ref))

  const toggleAll = (): void => {
    onSelected?.(allOn ? new Set() : new Set(rowRefs))
  }

  const toggleOne = (ref: string): void => {
    const next = new Set(selected)
    if (next.has(ref)) next.delete(ref)
    else next.add(ref)
    onSelected?.(next)
  }

  return (
    <>
      {/*
        How much of the set is on screen, both numbers the server's: the rows loaded
        and the summary's `matching` for the same filters. "10,000+" when the count
        stopped at its cap.
      */}
      {matching === undefined || matching.count === 0 || pages.isStale ? null : (
        <div>
          <Small>
            <Muted>
              Showing {format.number(rows.length)} of {format.number(matching.count)}
              {matching.capped ? '+' : ''}
            </Muted>
          </Small>
        </div>
      )}
      {/*
        The table scrolls, the page does not.

        A `<table>` is as wide as its columns demand, and nine of them — two of which
        are selects — are wider than 390px and wider than a 1024px window beside the
        sidebar. With nothing between it and the page, the *document* took the
        overflow: every page on the site slid sideways, filters and hero included.
        `ScrollX` is the primitive for exactly this and carries the note that says so.
      */}
      <ScrollX>
        {/* A table with nine columns of controls and no name is "table" to a reader. */}
        <Table aria-label="Tasks, one row each" aria-busy={pages.isStale}>
          <THead>
            <Tr>
              <Th>
                <input
                  type="checkbox"
                  checked={allOn}
                  ref={(el) => {
                    if (el !== null) el.indeterminate = someOn && !allOn
                  }}
                  aria-label="Select all loaded tasks"
                  onChange={toggleAll}
                />
              </Th>
              <Th>Task</Th>
              <Th>Notes</Th>
              <SortTh column="client" label="Client" sort={sort} dir={dir} onSort={onSort} />
              <SortTh column="who" label="Who" sort={sort} dir={dir} onSort={onSort} />
              <SortTh column="due" label="Due" sort={sort} dir={dir} onSort={onSort} />
              <Th>Status</Th>
              <Th>Priority</Th>
              <Th>Queue</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((task) => (
              <TaskRow
                key={task.ref}
                task={task}
                name={task.client === null ? null : clientName(task.client)}
                at={at}
                clientMuted={clientMuted}
                viewerId={viewerId}
                selected={selected.has(task.ref)}
                onToggle={() => {
                  toggleOne(task.ref)
                }}
                saved={notice.flashed === task.ref}
                onFieldSaved={field.saved}
                onMoved={field.moved}
                onCellError={field.failed}
              />
            ))}
          </TBody>
        </Table>
      </ScrollX>
      <LoadMore pages={pages} onLoadMore={loadMore} what="tasks" />
    </>
  )
}

/**
 * The foot of a paged list: Load more while the server has a next page, and what
 * went wrong when the last one did not come.
 *
 * A button, not a scroll trigger (Roberto, 18 Sep): it can be reached and pressed
 * from the keyboard, and it says what it will do. Disabled while a page is on its
 * way, so a second press is not a second request for the same page. A failure keeps
 * every row already loaded and offers the same page again. The board's columns use it
 * too, one each.
 */
export function LoadMore({
  pages,
  onLoadMore,
  what,
}: {
  readonly pages: TaskPages
  readonly onLoadMore: () => void
  /** Whose rows, for the button's accessible name: "tasks", or a column's name. */
  readonly what: string
}) {
  return (
    <>
      {pages.failed === null ? null : (
        <Notice role="alert">
          {pages.rows.length === 0
            ? `The ${what} did not load.`
            : pages.refreshFailed
              ? `The ${what} could not be read again.`
              : `The next ${what} did not load.`}{' '}
          {messageOf(pages.failed)}{' '}
          <Button size="sm" onClick={onLoadMore}>
            Try again
          </Button>
        </Notice>
      )}
      {pages.hasMore && pages.failed === null ? (
        <Button
          size="sm"
          onClick={onLoadMore}
          disabled={pages.loadingMore}
          aria-label={`Load more ${what}`}
        >
          {pages.loadingMore ? 'Loading…' : 'Load more'}
        </Button>
      ) : null}
    </>
  )
}

function SortTh({
  column,
  label,
  sort,
  dir,
  onSort,
}: {
  readonly column: ListSort
  readonly label: string
  readonly sort: ListSort
  readonly dir: ListDir
  readonly onSort: (column: ListSort) => void
}) {
  const on = sort === column
  const ariaSort = on ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'
  return (
    <Th aria-sort={ariaSort}>
      <button
        type="button"
        className="th-sort"
        onClick={() => {
          onSort(column)
        }}
      >
        {label}
        {on ? (dir === 'asc' ? ' ↑' : ' ↓') : null}
      </button>
    </Th>
  )
}

type TaskRowProps = {
  readonly task: TaskView
  /** The company as everybody says it, or null for the studio's own work. */
  readonly name: string | null
  readonly at: Date
  /** Restricted staff: name only — no link into the clients book. */
  readonly clientMuted: boolean
  /** Who is reading — the sub-line under a name is for colleagues, not for them. */
  readonly viewerId: string | null
  readonly selected: boolean
  /** Just written to — the row says so for a moment, as the strip says what. */
  readonly saved: boolean
  readonly onToggle: () => void
  /** After a reversible list save (#12/#25). */
  readonly onFieldSaved: (undo: FieldUndo) => void
  /** After a move made in the Status cell: said on the same line, with no way back. */
  readonly onMoved: (moved: TaskMoved) => void
  /** Why an in-cell save was refused, for the list's own alert line. */
  readonly onCellError: (message: string) => void
}

function TaskRow({
  task,
  name,
  at,
  clientMuted,
  viewerId,
  selected,
  saved,
  onToggle,
  onFieldSaved,
  onMoved,
  onCellError,
}: TaskRowProps) {
  const note = task.blocked_reason ?? (task.instructions === '' ? null : task.instructions)
  const hold = holdLine(task, at, viewerId)
  /*
   * A finished task is a record, and a record is read (#21).
   *
   * `tasks_update` still takes a done task's priority and `tasks_assign` still hands
   * it to somebody, so the row drew five controls that changed a thing nobody is
   * working on — and a Status select whose only option was the word "Done", which is
   * a menu offering nothing. The machine already knows: `isTerminal` is the same
   * question `RowActions` asks before it draws a move.
   */
  const closed = taskMachine.isTerminal(task.status)

  return (
    <Tr variant={taskState(task, at)} saved={saved}>
      <Td>
        <input
          type="checkbox"
          checked={selected}
          aria-label={`Select ${task.ref}`}
          onChange={onToggle}
        />
      </Td>
      <Td>
        {/*
          The widest column, said out loud.
          A table lays itself out by what its cells demand, and a title wraps at every
          space — so the Task column asked for one word and the Due column, which
          cannot break its stamp, took the room. `tlist__title` is the floor that puts
          the width back where the reading is.
        */}
        <span className="tlist__title">
          <Linkish asChild>
            <Link to="/studio/tasks/$ref" params={{ ref: task.ref }} id={rowTitleId(task.ref)}>
              {task.title}
            </Link>
          </Linkish>
          <div>
            <Xs>
              <Muted>
                <Ref value={task.ref} />
              </Muted>
            </Xs>
          </div>
        </span>
      </Td>
      <Td>
        {task.blocked_reason !== null ? (
          <span className="tlist__note" title={task.blocked_reason}>
            <Muted>Blocked · </Muted>
            {task.blocked_reason}
          </span>
        ) : (
          <ActionDialog
            action="tasks_update"
            label="Edit notes"
            title="Edit notes"
            submitLabel="Save"
            fitContent={task.instructions}
            fixed={{ task_ref: task.ref }}
            only={['instructions']}
            defaults={{ instructions: task.instructions }}
            trigger={
              <CellTrigger aria-label={`Edit notes for ${task.ref}`}>
                {note === null ? (
                  <Muted>—</Muted>
                ) : (
                  <span className="tlist__note" title={note}>
                    {note}
                  </span>
                )}
              </CellTrigger>
            }
          />
        )}
      </Td>
      <Td>
        {task.client === null || name === null ? (
          <Muted>{STUDIO_COLUMN}</Muted>
        ) : (
          <ClientLink client={task.client} name={name} muted={clientMuted} />
        )}
      </Td>
      <Td>
        <div className="tlist__who">
          {closed ? (
            <span>{task.assignee_name ?? <Muted>Unassigned</Muted>}</span>
          ) : (
            <CellAssign
              taskRef={task.ref}
              status={task.status}
              kind={task.assignee_kind}
              id={task.assignee_id}
              name={task.assignee_name}
              label={`Who for ${task.ref}`}
              onSaved={(previous, next, released, since) => {
                onFieldSaved({
                  kind: 'assign',
                  taskRef: task.ref,
                  since,
                  previous,
                  next,
                  released,
                })
              }}
              onError={onCellError}
            />
          )}
          <RowActions task={task} />
        </div>
        {/*
         * The hold used to ride inside the Who text, and a select cannot carry a
         * clause. It keeps its own line — but only when it says something a reader
         * would act on: a colleague actually on the task, or a claim that ran out.
         * `holdLine` is the rule, shared with the card so the two cannot diverge.
         */}
        {hold === null ? null : (
          <Xs block>
            <Muted>{hold}</Muted>
          </Xs>
        )}
      </Td>
      <Td date>
        {closed ? (
          <TaskWhen task={task} at={at} bare />
        ) : (
          <CellDue
            taskRef={task.ref}
            value={task.due_at}
            stamp={task.updated_at}
            label={`Due date for ${task.ref}`}
            /* The row's date, or the one on its way to it: one reading for both. */
            display={(dueAt) => <TaskWhen task={{ ...task, due_at: dueAt }} at={at} bare />}
            onSaved={(previous, next, since) => {
              onFieldSaved({ kind: 'due', taskRef: task.ref, since, previous, next })
            }}
            onError={onCellError}
          />
        )}
      </Td>
      <Td>
        {/*
          The status, as the moves the machine allows from here — never a free list
          of states (#107, correction round 1). `CellStatus` is the rule; this cell
          only gives it the reader. A finished task has no moves, so it is the word.

          And it gives it the page's line: a move changes the resting word of a select
          and nothing else, so without `onMoved` the row became something else in
          silence — which of forty, and by whose click, was nowhere on the screen.
        */}
        {closed ? (
          STATUS_WORD[task.status]
        ) : (
          <CellStatus
            task={task}
            viewerId={viewerId}
            onMoved={(move, landed) => {
              onMoved({ taskRef: task.ref, move, landed })
            }}
            onError={onCellError}
          />
        )}
      </Td>
      <Td>
        {closed ? (
          priorityWord(task.priority)
        ) : (
          <CellEdit
            taskRef={task.ref}
            field="priority"
            value={task.priority}
            label={`Priority for ${task.ref}`}
            onSaved={(previous, next, since) => {
              onFieldSaved({
                kind: 'priority',
                taskRef: task.ref,
                since,
                previous: Number(previous),
                next: Number(next),
              })
            }}
            onError={onCellError}
          />
        )}
      </Td>
      <Td>
        {closed ? (
          QUEUE_WORD[task.queue]
        ) : (
          <CellEdit
            taskRef={task.ref}
            field="queue"
            value={task.queue}
            label={`Queue for ${task.ref}`}
            onSaved={(previous, next, since) => {
              onFieldSaved({
                kind: 'queue',
                taskRef: task.ref,
                since,
                previous: previous as TaskQueue,
                next: next as TaskQueue,
              })
            }}
            onError={onCellError}
          />
        )}
      </Td>
    </Tr>
  )
}

/**
 * Cell content as a button — looks like the cell, opens a dialog.
 *
 * `.cell-btn`, not `.th-sort`: the second is the **sortable column head**'s style,
 * and a Notes cell wearing it said, in markup a reviewer reads, that pressing it
 * would sort the table. Same look, its own name.
 */
const CellTrigger = forwardRef<
  HTMLButtonElement,
  { readonly children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>
>(function CellTrigger({ children, className, type, ...props }, ref) {
  return (
    <button ref={ref} type={type ?? 'button'} className={className ?? 'cell-btn'} {...props}>
      {children}
    </button>
  )
})

/** The id on a row's title link, which is what Load more gives the focus to. */
function rowTitleId(ref: string): string {
  return `task-row-${ref}`
}
